import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Same-origin API on preview + deployment: the frontend host and /api backend
// share the same domain, so CORS is not evaluated and httpOnly cookies flow
// bidirectionally when `withCredentials: true` is set on the axios instance.
export const http = axios.create({ baseURL: API, timeout: 15000, withCredentials: true });

// Silent auto-refresh: when any request comes back 401 we try `/auth/refresh`
// exactly once (using the httpOnly `refresh_token` cookie) and replay the
// original request. Simultaneous 401s share the same refresh promise so we
// never issue two refresh calls at once.
let refreshPromise = null;

http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    // Guardrails: only auto-refresh on 401, never for the refresh/login/logout
    // endpoints themselves (avoids infinite loops), and never for already-
    // retried requests. Crucially /auth/me and /brand/me MUST be retryable —
    // they are the primary hydration calls that a silent refresh unlocks.
    const isRefreshEndpoint = /\/(auth|brand\/auth)\/(refresh|login|logout)/.test(url);
    if (status !== 401 || !original || original._retried || isRefreshEndpoint) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = http.post("/auth/refresh").finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      return http.request(original);
    } catch (refreshErr) {
      return Promise.reject(error);
    }
  },
);

export const fetchBrands = (params = {}) => http.get("/brands", { params }).then((r) => r.data);
export const fetchBrand = (slug) => http.get(`/brands/${slug}`).then((r) => r.data);
// Admin-only lookup that bypasses the public visibility gate. Used by
// AdminBrandEdit so DRAFT / NEEDS_ATTENTION brands can still be opened
// from the Freshness queue etc.
export const fetchAdminBrand = (slug) => http.get(`/admin/brands/${slug}`).then((r) => r.data);
export const fetchRecent = () => http.get("/recent-profiles").then((r) => r.data);
export const fetchSectors = () => http.get("/sectors").then((r) => r.data);
export const fetchSiteStats = () => http.get("/site-stats").then((r) => r.data);
export const fetchSector = (code) => http.get(`/sectors/${code}`).then((r) => r.data);
export const searchBrands = (q) => http.get("/search", { params: { q } }).then((r) => r.data);
export const fetchPartners = () => http.get("/partners").then((r) => r.data);
export const followBrand = (payload) => http.post("/followers", payload).then((r) => r.data);
export const submitReport = (payload) => http.post("/reports", payload).then((r) => r.data);
export const submitSuggestion = (payload) => http.post("/suggestions", payload).then((r) => r.data);
export const submitMissingBrand = (payload) => http.post("/missing-brand-requests", payload).then((r) => r.data);
